import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { getReadyBy } from '../../../helpers/common';
import { RowData } from '../../../new-lib/types/common';
import Link from 'next/link';
import LongArrowIcon from '../../../new-components/svg-icons/LongArrow';
import { useRouter } from 'next/router';
import { setSelectedCompound } from '../../../redux/slices/mapSlice';
import { useDispatch } from 'react-redux';

interface TableProps {
  property: any;
  dataPerRow: number;
}

interface RowProps {
  values: RowData;
  mapAvailable: any;
}

interface ContainerProps {
  dataPerRow: number;
  locale?: string;
}

const Row = ({ values, mapAvailable }: RowProps) => {
  return (
    <div className="rowData">
      <div>{values.key}</div>
      {values.values.map(value =>
        values.linkTo ? (
          <Link
            key={values.key}
            href={`/${values.linkTo}`}
            className="compound-link"
            onClick={mapAvailable}
          >
            <span key={value}>{value}</span>
            <div className="arrow-icon">
              <LongArrowIcon />
            </div>
          </Link>
        ) : (
          <div key={values.key}>{value}</div>
        )
      )}
    </div>
  );
};

const PropertyDetailsTable = ({ property, dataPerRow }: TableProps) => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();
  const header = {
    name: property.property_type.name,
    area: { min: property.min_unit_area, max: property.max_unit_area },
    span: t('m²')
  };

  const dispatch = useDispatch();

  const mapAvailable = () => {
    if (property?.compound?.on_map) {
      dispatch(setSelectedCompound(property.compound));
    }
  };

  const getRedirect = () => {
    if (property?.compound?.on_map) {
      return `map`;
    } else {
      return `compound/${property.compound.slug}`;
    }
  };

  const propertyTableDetails = [
    property.id
      ? {
          key: t('reference_number'),
          values: [property.id]
        }
      : null,
    property.number_of_bedrooms
      ? {
          key: t('bedrooms'),
          values: [property.number_of_bedrooms]
        }
      : null,
    property.number_of_bathrooms
      ? {
          key: t('bathrooms'),
          values: [property.number_of_bathrooms]
        }
      : null,
    property.min_ready_by
      ? {
          key: t('delivery_in'),
          values: [t(getReadyBy(property.min_ready_by))]
        }
      : null,
    property.compound.name
      ? {
          key: t('compound'),
          values: [property.compound.name],
          linkTo: [getRedirect()]
        }
      : null,
    property.financing != null || property.resale != null
      ? {
          key: t('sale_type'),
          values: [
            property.financing === true
              ? 'Nawy Now'
              : property.resale === true
                ? t('resale')
                : t('developer_sale')
          ]
        }
      : null,
    property.finishing
      ? {
          key: t('finishing'),
          values: [t(property.finishing)]
        }
      : null
  ];

  const rowValues = propertyTableDetails.filter(curr => {
    return curr !== null;
  }) as Array<RowData>;

  return (
    <Container dataPerRow={dataPerRow} locale={locale}>
      <div className="header">
        <div>{header.name}</div>
        <div>
          {header.area.min != 0 ? (
            <>
              {header.area.min}
              <span>{header.span}</span>
            </>
          ) : null}
          {header.area.min != header.area.max && header.area.max != null && (
            <>
              <span> ~ </span>
              {header.area.max}
              <span>{header.span}</span>
            </>
          )}
        </div>
      </div>
      {rowValues.map(rowValue => (
        <Row values={rowValue} key={rowValue.key} mapAvailable={mapAvailable} />
      ))}
    </Container>
  );
};

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  .header {
    display: flex;
    align-items: center;
    width: 100%;
    padding-bottom: 8px;
    font-family: materialBold;
    font-size: ${props => props.theme.font.sizes.xxl};
    color: ${props => props.theme.light.colors.primaryBlue};
    border: 0.5px solid ${props => props.theme.light.colors.black};
    border-width: 0px 0px 0.5px 0px;
    div {
      width: calc(100% / ${props => props.dataPerRow});
      &:last-child {
        font-size: ${props => props.theme.font.sizes.xxl};
        span {
          font-size: ${props => props.theme.font.sizes.s};
          color: ${props => props.theme.light.colors.black};
          font-family: materialRegular;
        }
      }
    }
  }
  .rowData {
    width: 100%;
    padding: 8px 4px;
    display: flex;
    align-items: center;
    font-family: materialMedium;
    font-size: ${props => props.theme.font.sizes.s};
    color: ${props => props.theme.light.colors.black};
    border: 0.5px solid ${props => props.theme.light.borderColors.primary};
    border-width: 0px 0px 0.5px 0px;

    &:nth-child(even) {
      background-color: #f4f4f4;
    }

    div {
      width: calc(100% / ${props => props.dataPerRow});
    }
    .compound-link {
      display: flex;
      flex-direction: row;
      gap: 8px;
      cursor: pointer;
      span {
        text-decoration: underline;
        color: #015c9a;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
      .arrow-icon {
        background: #d9d9d9;
        border-radius: 50%;
        padding: 5px;
        width: 19px;
        height: 19px;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: ${props => (props.locale == 'ar' ? 'rotate(180deg)' : '')};
      }
    }
  }
`;
export default PropertyDetailsTable;
